import { useNavigate } from "react-router-dom";
import { ArrowLeft, Swords, Trophy, Target, TrendingUp, Users, Layers } from "lucide-react";

const BPL_LOGO = "https://customer-assets.emergentagent.com/job_pickle-os/artifacts/cj28ut5a_AFEA36B2-2E75-4ED3-9048-93AFF9BF75C1.png";

const H = ({ icon: Icon, children }) => (
  <h2 className="font-display font-extrabold uppercase tracking-wide text-xl sm:text-2xl text-pb-red mt-10 mb-3 flex items-center gap-2">
    {Icon && <Icon className="w-5 h-5 shrink-0" />} {children}
  </h2>
);

const P = ({ children }) => <p className="text-sm sm:text-base text-zinc-300 leading-relaxed mb-3">{children}</p>;

const UL = ({ items }) => (
  <ul className="space-y-1.5 mb-3">
    {items.map((it, i) => (
      <li key={i} className="flex items-start gap-2 text-sm sm:text-base text-zinc-300">
        <span className="text-pb-red mt-1.5 w-1.5 h-1.5 bg-pb-red shrink-0" /> {it}
      </li>
    ))}
  </ul>
);

export default function LeagueOverview() {
  const navigate = useNavigate();
  return (
    <div className="max-w-3xl mx-auto" data-testid="league-overview-page">
      <button data-testid="overview-back-btn" onClick={() => navigate("/league")}
        className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-pb-red transition-colors mb-8">
        <ArrowLeft className="w-4 h-4" /> Back to BPL League
      </button>

      <div className="text-center mb-10">
        <img src={BPL_LOGO} alt="BPL League" data-testid="overview-bpl-logo"
          className="w-48 h-48 sm:w-56 sm:h-56 object-contain mx-auto mb-4" />
        <h1 className="font-display font-black uppercase tracking-wide text-2xl sm:text-3xl">Official League Overview</h1>
      </div>

      <h2 className="font-display font-extrabold uppercase text-2xl sm:text-3xl mb-3">BPL League — Where Competition Meets Development</h2>
      <P>Welcome to the BPL League — a new kind of pickleball experience designed to do more than just track wins and losses. This is where structured competition, skill development, and real performance data come together to help you elevate your game.</P>
      <P>Whether you're just starting out or pushing toward advanced play, the BPL League gives you a clear path to improve, compete, and measure your growth.</P>

      <H icon={Swords}>What Makes the BPL League Different?</H>
      <P>Most leagues focus only on match results. The BPL League goes further.</P>
      <P>This system is built on two key pillars:</P>
      <UL items={[
        <><span className="font-bold text-white">Game Play (Match Results)</span> — where wins and losses count</>,
        <><span className="font-bold text-white">Skill Battles (Performance Scoring)</span> — where development earns team points</>,
      ]} />
      <P>This means every session matters — not just the final score.</P>

      <H icon={Layers}>How It Works</H>
      <P>Each league session is divided into two parts:</P>
      <div className="bg-pb-surface border border-pb-border p-5 mb-4">
        <h3 className="font-display font-bold uppercase text-lg text-pb-cyan mb-2">1. Skill Battles (Team Point System)</h3>
        <P>These are structured, high-rep competitive drills designed to sharpen specific areas of your game. Examples include:</P>
        <UL items={["Dink Wars", "Transition Wars"]} />
        <P>In these battles:</P>
        <UL items={["Players compete in controlled formats", "Performance earns team points", "Focus is on execution, consistency, and pressure handling"]} />
        <P><span className="font-bold text-white">Important:</span> Skill Battles do not count toward your match record — they build your performance foundation.</P>
      </div>
      <div className="bg-pb-surface border border-pb-border p-5 mb-4">
        <h3 className="font-display font-bold uppercase text-lg text-pb-cyan mb-2">2. Game Play (Match System)</h3>
        <P>This is where traditional competition takes place.</P>
        <UL items={["Matches are played in a structured format", "Wins and losses are recorded", "Results impact standings and rankings"]} />
        <P>This is where you apply what you sharpened during Skill Battles.</P>
      </div>

      <H icon={Target}>Why This System Works</H>
      <P>The BPL League bridges the gap between practice and competition.</P>
      <P>Most players struggle because:</P>
      <UL items={["Practice doesn't translate into matches", "Matches don't isolate weaknesses"]} />
      <P>The BPL solves this by:</P>
      <UL items={["Training under pressure before matches", "Tracking both skill execution and game results", "Giving players a complete picture of their performance"]} />

      <H icon={Trophy}>Skill Levels &amp; Divisions</H>
      <P>The league is structured to meet players where they are:</P>
      <UL items={[
        <><span className="font-bold text-white">Low Intermediate Division</span> — Learning fundamentals, building consistency</>,
        <><span className="font-bold text-white">Intermediate Division</span> — Developing control, strategy, and transitions</>,
        <><span className="font-bold text-white">Advanced Division</span> — High-speed execution, precision, and competitive play</>,
      ]} />
      <P>Each level ensures you're challenged without being overwhelmed.</P>

      <H icon={TrendingUp}>Performance Tracking &amp; Growth Intelligence</H>
      <P>Every player's journey is tracked. Inside the BPL system, you'll be able to:</P>
      <UL items={["Record match results", "Track skill battle performance", "Monitor improvement over time", "Receive insights into strengths and weaknesses"]} />
      <P>This isn't guesswork — it's measurable growth.</P>

      <H icon={Users}>Who This Is For</H>
      <P>The BPL League is for players who:</P>
      <UL items={["Want to improve, not just play", "Enjoy structured, competitive environments", "Are serious about tracking their progress", "Thrive in both team and individual settings"]} />

      <H>More Than a League</H>
      <P>This is more than just weekly games. It's a system designed to:</P>
      <UL items={["Build better players", "Create competitive consistency", "Develop confidence under pressure"]} />

      <div className="bg-pb-surface border border-pb-red/40 p-6 text-center mt-10 mb-6">
        <h2 className="font-display font-black uppercase text-2xl mb-3">Ready to Compete and Improve?</h2>
        <P>The BPL League is your opportunity to step into a smarter, more effective way to play pickleball.</P>
        <p className="font-display font-bold uppercase tracking-wide text-pb-red text-lg leading-snug mb-2">
          Join the league. Track your growth. Elevate your game.
        </p>
        <p className="text-sm text-zinc-400 mb-5">Your development starts here.</p>
        <button data-testid="overview-roster-btn" onClick={() => navigate("/league/register")}
          className="bg-pb-red text-black font-bold uppercase tracking-widest px-8 py-3 hover:bg-[#BEF264] active:scale-95 transition-all">
          Roster for the League
        </button>
      </div>
    </div>
  );
}
